package ai.wagemix.web.screens.sharedlinks

import ai.wagemix.web.screens.WMLockedState
import ai.wagemix.logic.sharedlinks.WMSharedLinkStats
import ai.wagemix.web.theme.WMLabelStrongTextStyle
import ai.wagemix.web.components.WMToggle
import ai.wagemix.logic.WMRoutes
import ai.wagemix.logic.sharedlinks.WMSharedLinkRow
import ai.wagemix.logic.sharedlinks.WMSharedLinksPresenter
import ai.wagemix.logic.sharedlinks.WMSharedLinksPresenter.Action
import ai.wagemix.logic.sharedlinks.WMSharedLinksPresenter.Event
import ai.wagemix.logic.sharedlinks.WMSharedLinksPresenter.Prompt
import ai.wagemix.net.models.WMBadge
import ai.wagemix.net.models.WMSharedMixPurpose
import ai.wagemix.web.components.WMButton
import ai.wagemix.web.components.WMButtonKind
import ai.wagemix.web.components.WMButtonSize
import ai.wagemix.web.components.WMDialog
import ai.wagemix.web.components.WMDialogButtonStyle
import ai.wagemix.web.components.WMEmptyState
import ai.wagemix.web.components.WMIconGlyph
import ai.wagemix.web.components.WMPanel
import ai.wagemix.web.components.WMTextField
import ai.wagemix.web.screens.WMPromptDialog
import ai.wagemix.web.screens.WMWebStrings
import ai.wagemix.web.screens.mixes.WMMixGridStyle
import ai.wagemix.web.screens.share.WMSharePreviewImage
import ai.wagemix.web.theme.WMAppContentGapVar
import ai.wagemix.web.theme.WMBodySmallTextStyle
import ai.wagemix.web.theme.WMInlineGapVar
import ai.wagemix.web.theme.WMTightGapVar
import androidx.compose.runtime.Composable
import androidx.compose.runtime.State
import androidx.compose.runtime.getValue
import androidx.compose.runtime.key
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.fsryan.foundation.logic.FSPrompt
import com.fsryan.foundation.web.components.FSLoadingIndicator
import com.fsryan.foundation.web.screens.FSWebScreen
import com.fsryan.foundation.web.theme.fs
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import com.varabyte.kobweb.silk.theme.colors.palette.toPalette
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime
import org.jetbrains.compose.web.dom.Div

/** The shared links page: every link the user has shared, each with its preview image and Regenerate when needed. */
@Composable
fun <P: Prompt, E: Event<P>, A: Action, PR: WMSharedLinksPresenter<P, E, A>, VM: WMBaseSharedLinksWebViewModel<P, E, A, PR>> WMSharedLinksScreen(
    createViewModel: () -> VM,
    strings: WMWebStrings,
    modifier: Modifier = Modifier
) {
    FSWebScreen(createViewModel = createViewModel) { vm ->
        WMSharedLinksScreenLayout(
            strings = strings,
            loadingState = vm.loadingState,
            rowsState = vm.rowsState,
            lapsedState = vm.lapsedState,
            modifier = modifier,
            onRegeneratePreview = vm::userRequestedRegeneratePreview,
            onSetActive = vm::userChangedShareActive
        )
        val prompt = vm.promptState.value
        WMPromptDialog(
            prompt = prompt,
            unwrap = { (it as? Prompt.ComposedSuperPrompt)?.wrapped },
            strings = strings,
            onResponse = { vm.userRespondedToPrompt(FSPrompt.Response(prompt, it)) }
        ) { p ->
            val respond = { i: Int -> vm.userRespondedToPrompt(FSPrompt.Response(prompt, i)) }
            when (p) {
                is Prompt.PreviewRegenerationFailed -> WMDialog(
                    title = strings.share.regenerateFailedTitle,
                    description = p.message ?: strings.share.regenerateFailedMessage,
                    onDismiss = { respond(Prompt.PreviewRegenerationFailed.Selection.Dismiss.ordinal) },
                    actions = {
                        WMButton(strings.common.close, modifier = WMDialogButtonStyle.toModifier()) {
                            respond(Prompt.PreviewRegenerationFailed.Selection.Dismiss.ordinal)
                        }
                    }
                ) {}
                is Prompt.ActivationFailed -> WMDialog(
                    title = strings.sharedLinks.activationFailedTitle,
                    description = p.message ?: strings.sharedLinks.activationFailedMessage,
                    onDismiss = { respond(Prompt.ActivationFailed.Selection.Dismiss.ordinal) },
                    actions = {
                        WMButton(strings.common.close, modifier = WMDialogButtonStyle.toModifier()) {
                            respond(Prompt.ActivationFailed.Selection.Dismiss.ordinal)
                        }
                    }
                ) {}
                else -> Unit
            }
        }
    }
}

@Composable
fun WMSharedLinksScreenLayout(
    strings: WMWebStrings,
    loadingState: State<Boolean>,
    rowsState: State<List<WMSharedLinkRow>?>,
    lapsedState: State<Boolean>,
    modifier: Modifier = Modifier,
    onRegeneratePreview: (sharedMixId: String) -> Unit,
    onSetActive: (sharedMixId: String, active: Boolean) -> Unit
) {
    val s = strings.sharedLinks
    val rows = rowsState.value
    if (lapsedState.value) {
        WMLockedState(strings.renewal.sharedLinksTitle, strings.renewal.sharedLinksMessage, strings, modifier)
        return
    }
    Column(modifier = Modifier.fillMaxWidth().gap(WMAppContentGapVar.value()).then(modifier)) {
        when {
            rows == null || loadingState.value && rows.isEmpty() -> FSLoadingIndicator(strings.common.loading)
            rows.isEmpty() -> WMEmptyState(WMIconGlyph.Share2, s.emptyTitle, s.emptyMessage) {
                WMButton(strings.shell.savedMixes, leadingIcon = WMIconGlyph.Bookmark) {
                    kotlinx.browser.window.location.href = WMRoutes.MIXES
                }
            }
            else -> {
                Div(WMMixGridStyle.toModifier().toAttrs { attr("role", "list") }) {
                    rows.forEach { row ->
                        key(row.sharedMixId) {
                            WMSharedLinkCard(
                                strings,
                                row,
                                onRegenerate = { onRegeneratePreview(row.sharedMixId) },
                                onSetActive = { active -> onSetActive(row.sharedMixId, active) }
                            )
                        }
                    }
                }
            }
        }
    }
}

/**
 * One shared link: its preview image, what was shared, on Pro how often it was opened, the switch that turns it off
 * and on, and the link to copy or open.
 */
@Composable
private fun WMSharedLinkCard(
    strings: WMWebStrings,
    row: WMSharedLinkRow,
    onRegenerate: () -> Unit,
    onSetActive: (Boolean) -> Unit
) {
    val s = strings.sharedLinks
    val colors = ColorMode.current.toPalette().fs
    var copied by remember(row.shareableUrl) { mutableStateOf(false) }
    WMPanel(title = row.title, modifier = Modifier.attrsModifier { attr("role", "listitem") }) {
        WMSharePreviewImage(strings, row.previewState, row.previewImageUrl, onRegenerate = onRegenerate)
        SpanText(row.meta(strings), WMBodySmallTextStyle.toModifier().color(colors.onSurfaceVariant))
        WMSharedLinkStatsLine(strings, row.stats)
        Row(modifier = Modifier.gap(WMInlineGapVar.value()), verticalAlignment = Alignment.CenterVertically) {
            WMToggle(
                checked = row.active,
                onCheckedChange = onSetActive,
                accessibilityString = s.linkSwitchLabel(row.title)
            )
            SpanText(
                if (row.active) s.linkOn else s.linkOff,
                WMBodySmallTextStyle.toModifier().color(if (row.active) colors.onSurface else colors.onSurfaceVariant)
            )
        }
        WMTextField(row.shareableUrl, onTextChange = {}, readOnly = true, leadingIcon = WMIconGlyph.Link)
        Row(modifier = Modifier.gap(WMInlineGapVar.value()), verticalAlignment = Alignment.CenterVertically) {
            WMButton(
                if (copied) s.copied else s.copyLink,
                size = WMButtonSize.Sm,
                leadingIcon = if (copied) WMIconGlyph.Check else WMIconGlyph.Copy
            ) {
                kotlinx.browser.window.navigator.clipboard.writeText(row.shareableUrl)
                copied = true
            }
            WMButton(
                s.openLink,
                kind = WMButtonKind.Secondary,
                size = WMButtonSize.Sm,
                leadingIcon = WMIconGlyph.ExternalLink
            ) {
                kotlinx.browser.window.open(row.shareableUrl, "_blank")
            }
        }
    }
}

/**
 * The card's statistics line (planning/share-link-stats-plan.md): the opens on Pro, an offer of Pro on a card whose
 * statistics are stored, or why a card shared before statistics has none.
 */
@Composable
private fun WMSharedLinkStatsLine(strings: WMWebStrings, stats: WMSharedLinkStats) {
    val s = strings.sharedLinks
    val colors = ColorMode.current.toPalette().fs
    when (stats) {
        is WMSharedLinkStats.Shown -> SpanText(stats.line(s), WMLabelStrongTextStyle.toModifier())
        WMSharedLinkStats.Locked -> Row(
            modifier = Modifier.gap(WMInlineGapVar.value()),
            verticalAlignment = Alignment.CenterVertically
        ) {
            SpanText(s.statsLocked, WMBodySmallTextStyle.toModifier().color(colors.onSurfaceVariant))
            WMButton(strings.common.viewPlans, kind = WMButtonKind.Secondary, size = WMButtonSize.Sm) {
                kotlinx.browser.window.location.href = WMRoutes.PRICING
            }
        }
        WMSharedLinkStats.Untracked -> {
            SpanText(s.statsUntracked, WMBodySmallTextStyle.toModifier().color(colors.onSurfaceVariant))
        }
        WMSharedLinkStats.Unavailable -> Unit   // not read yet, or refused: no figures rather than wrong ones
    }
}

/** "12 opens (3 while off) · Last opened 2026-10-27 14:05", or "Not opened yet". */
private fun WMSharedLinkStats.Shown.line(s: WMWebStrings.SharedLinks): String {
    if (clickCount == 0L) {
        return s.notOpenedYet
    }
    val last = lastClickedAt?.toLocalDateTime(TimeZone.currentSystemDefault())?.let { local ->
        val time = "${local.hour.toString().padStart(2, '0')}:${local.minute.toString().padStart(2, '0')}"
        s.lastOpened("${local.date} $time")
    }
    return listOfNotNull(s.opens(clickCount, deactivatedClickCount), last).joinToString(" · ")
}

/** "Shared 2026-10-26 · Annual · Job posting · Gold": when, which figures, what for, and the badge it earned. */
private fun WMSharedLinkRow.meta(strings: WMWebStrings): String {
    val s = strings.sharedLinks
    return listOfNotNull(
        createdAt?.let { s.sharedOn(it.toLocalDateTime(TimeZone.currentSystemDefault()).date.toString()) },
        if (hourly) strings.common.hourly else strings.common.annual,
        when (purpose) {
            WMSharedMixPurpose.JD -> s.jobPosting
            WMSharedMixPurpose.Social -> s.social
            WMSharedMixPurpose.Unknown -> null
        },
        badge.takeIf { it != WMBadge.None }?.let { strings.share.tierName(it) },
    ).joinToString(" · ")
}
